
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LanguageSelector, { SupportedLanguage } from "@/components/multilingual/LanguageSelector";
import { 
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { translations, defaultLanguage, LANGUAGE_COOKIE_NAME, detectBrowserLanguage } from "@/lib/i18n";
import { DOCUMENT_VERSIONS } from "@/lib/constants/documentVersions";

const formatVersionDate = (version: string, language: string): string => {
  const locale = language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US';
  try {
    const date = new Date(version);
    if (isNaN(date.getTime())) return version;
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return version;
  }
};

const Copyright = () => {
  const [language, setLanguage] = useState<SupportedLanguage>(defaultLanguage);

  useEffect(() => {
    // Check saved language preference
    const savedLanguage = localStorage.getItem(LANGUAGE_COOKIE_NAME);
    if (savedLanguage && ['en', 'de', 'fr', 'es'].includes(savedLanguage)) {
      setLanguage(savedLanguage as SupportedLanguage);
    } else {
      // If no saved language, detect browser language
      const browserLang = detectBrowserLanguage();
      setLanguage(browserLang);
      localStorage.setItem(LANGUAGE_COOKIE_NAME, browserLang);
    }
  }, []);
  
  const t = translations.legalPages[language]?.copyright;
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-grow pt-24">
        <div className="max-w-4xl mx-auto px-4 pt-8 pb-16">
          <div className="flex flex-wrap items-center justify-between mb-6">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{t?.breadcrumb}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <div className="mt-2 sm:mt-0">
              <LanguageSelector language={language} onChange={setLanguage} />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold mb-6">{t?.title}</h1>
          
          <div className="prose prose-slate max-w-none">
            {t?.content.map((section, index) => (
              <div key={index}>
                <h2>{section.title}</h2>
                <p className="whitespace-pre-line">{section.text}</p>
              </div>
            ))}
            
            <p className="text-sm text-muted-foreground mt-8">
              {language === 'de' ? 'Zuletzt aktualisiert' :
               language === 'fr' ? 'Dernière mise à jour' :
               language === 'es' ? 'Última actualización' :
               'Last updated'}: {formatVersionDate(DOCUMENT_VERSIONS.copyrightPolicy, language)}
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Copyright;
